import React from 'react';
import { cn } from '@hts/ui';

const VARIANTS = {
  // ticket status
  New:         'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'In Progress': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Waiting:     'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  Escalated:   'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  Resolved:    'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  Closed:      'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  // sla
  critical:    'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  warning:     'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  ok:          'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  // mode
  shadow:      'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  recommend:   'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  auto:        'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  // priority
  p1: 'bg-red-600 text-white',
  p2: 'bg-orange-500 text-white',
  p3: 'bg-amber-400 text-amber-900',
  p4: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  p5: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
} as const;

export function StatusBadge({ label, variant }: { label: string; variant?: keyof typeof VARIANTS }) {
  const cls = VARIANTS[variant ?? (label as keyof typeof VARIANTS)] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  return (
    <span className={cn('chip inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold', cls)}>
      {label}
    </span>
  );
}

export function PriorityBadge({ priority }: { priority: number }) {
  const v = `p${priority}` as keyof typeof VARIANTS;
  const label = `P${priority}`;
  return <StatusBadge label={label} variant={v} />;
}

export function SlaChip({ risk, deadline }: { risk: 'critical' | 'warning' | 'ok'; deadline: string }) {
  const mins = Math.round((new Date(deadline).getTime() - Date.now()) / 60_000);
  const label = mins < 0
    ? `${Math.abs(mins)}m overdue`
    : mins < 60 ? `${mins}m` : `${Math.round(mins / 60)}h`;
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
      VARIANTS[risk]
    )}>
      <span className={cn('h-1.5 w-1.5 rounded-full',
        risk === 'critical' ? 'bg-red-500 animate-pulse' :
        risk === 'warning' ? 'bg-amber-500' : 'bg-green-500'
      )} />
      {label}
    </span>
  );
}

export function SentimentChip({ score }: { score: number }) {
  const config = {
    1: { label: 'Very negative', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', emoji: '😡' },
    2: { label: 'Negative', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', emoji: '😟' },
    3: { label: 'Neutral', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300', emoji: '😐' },
    4: { label: 'Positive', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', emoji: '🙂' },
    5: { label: 'Very positive', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', emoji: '😊' },
  }[Math.round(score) as 1|2|3|4|5] ?? { label: 'Unknown', cls: '', emoji: '?' };
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', config.cls)}>
      <span aria-hidden="true">{config.emoji}</span>
      {config.label}
    </span>
  );
}

export function ModeBadge({ mode }: { mode: 'shadow' | 'recommend' | 'auto' }) {
  const icons = { shadow: '👁', recommend: '💡', auto: '⚡' };
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', VARIANTS[mode])}>
      <span aria-hidden="true">{icons[mode]}</span>
      {mode.charAt(0).toUpperCase() + mode.slice(1)}
    </span>
  );
}
